import { Prize } from 'functions/src/types/firestore/Game/Competition';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { AvatarNext } from '../AvatarNext';
import { GradientIcon } from '../gradients/GradientIcon';
import ShieldIcon from '@mui/icons-material/Shield';
import Typography from '@mui/material/Typography';
import { useMobile } from 'src/hooks/useMobile';
import { GradientTypography } from '../gradients/GradientTypography';
import { useNumberFormatter } from 'src/hooks/useNumberFormatter';
import { TypographyTruncated } from '../TypographyTruncated';

export type FeaturedCompetitorProps = {
  imgUrl?: string;
  name: string;
  rank: number;
  prize?: Prize;
  score: string | number | Date;
  scoreSuffix?: string;
};

export function FeaturedCompetitor({
  imgUrl,
  name,
  rank,
  prize,
  score,
  scoreSuffix,
}: FeaturedCompetitorProps) {
  const isMobile = useMobile();
  const formatterCompact = useNumberFormatter({
    notation: 'compact',
    maximumSignificantDigits: 3,
  });
  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        height: 'inherit',
        mx: 4,
        my: 4,
      }}
    >
      <Box
        sx={{
          borderRadius: '100%',
          border: 'solid 4px transparent',
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          maxWidth: '100px',
          maxHeight: '100px',
          position: 'relative',
        }}
      >
        {!!imgUrl && (
          <AvatarNext
            variant="gradientOutlined"
            src={imgUrl}
            alt={name}
            sx={{
              width: '100px',
              height: '100px',
            }}
          />
        )}
        <GradientIcon
          IconComponent={ShieldIcon}
          gradientColor={
            rank === 1
              ? 'linear-gradient(270deg, #FFF6C2 0%, #CCAD00 100%)'
              : rank === 2
              ? 'linear-gradient(270deg, #EBEBEB 0%, ##7A7A7A 100%)'
              : 'linear-gradient(270deg, #E7C29C 0%, #945C24 100%)'
          }
          sx={{
            position: !!imgUrl ? 'absolute' : undefined,
            bottom: !!imgUrl ? -24 : undefined,
            right: !!imgUrl ? -6 : undefined,
            height: '62px',
            width: '45.5px',
          }}
        />
        <Typography
          variant="h4"
          color="text.primary"
          sx={{
            position: 'absolute',
            bottom: !!imgUrl ? -10 : 18,
            right: !!imgUrl ? (rank === 1 ? 12 : 8) : rank === 1 ? 17 : 13,
          }}
        >
          {rank}
        </Typography>
      </Box>
      <Box sx={{ ml: 4, mt: isMobile ? 4 : 0 }}>
        <TypographyTruncated
          text={name}
          textMaxLength={20}
          variant="h6"
          sx={{ textAlign: 'center' }}
        />
        <Stack flexDirection="row" justifyContent="center" alignItems="center">
          {!!prize && (
            <Box sx={{ mr: 4 }}>
              <GradientTypography
                gradientColor="secondary.vertical"
                variant="h5"
                sx={{ textAlign: 'center' }}
              >
                {formatterCompact.format(prize.amount)}
              </GradientTypography>

              <GradientTypography
                gradientColor="secondary.vertical"
                variant="h6"
                sx={{ textAlign: 'center' }}
              >
                {prize?.currency}
              </GradientTypography>
            </Box>
          )}
          {!!score && (
            <Box>
              <GradientTypography
                gradientColor="error.vertical"
                variant="h5"
                sx={{ textAlign: 'center' }}
              >
                {`${score}`}
              </GradientTypography>
              {!!scoreSuffix && (
                <GradientTypography
                  gradientColor="error.vertical"
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    textTransform: 'uppercase',
                  }}
                >
                  {scoreSuffix}
                </GradientTypography>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
